'use client'

import { useMemo } from 'react'
import { SeparationLine } from '@/components/separation-line'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import { RestaurantDetailLocation } from '@/features/restaurants/restaurant-detail/restaurant-detail-location/restaurant-detail-location'
import { RestaurantDetailMobileCta } from '@/features/restaurants/restaurant-detail/restaurant-detail-mobile-cta'
import { RestaurantDetailOpeningHours } from '@/features/restaurants/restaurant-detail/restaurant-detail-opening-hours/restaurant-detail-opening-hours'
import { RestaurantPriceRange } from '@/features/restaurants/restaurant-price-range'
import { RestaurantTags } from '@/features/restaurants/restaurant-tags'
import { Rating } from '@/features/reviews/rating'
import { cn } from '@/utils/styles/cn'
import { useMobile } from '@/utils/window/use-mobile'

export const RestaurantDetailHeader = () => {
    const { restaurant } = useRestaurantDetailContext()

    const isMobile = useMobile()

    const tagNames = useMemo(() => restaurant?.tags?.map((tag) => tag.name), [restaurant?.tags])

    if (!restaurant) return null

    const ratingNode =
        typeof restaurant.avgRating === 'number' && restaurant.avgRating !== 0 ? (
            <Rating
                rating={restaurant.avgRating}
                withBrackets
                ratingCount={restaurant.ratingsCount}
                ratingCountStyleType="count-and-label"
                starClassName="h-[14px] md:h-[20px] w-[14px] md:w-[20px]"
                withReadableRating
                starsStyleType="maximal"
                labelClassName="text-p-xs md:text-p-md"
                ratingClassName="text-p-xs md:text-p-md"
                starsClassName="top-[-1px] md:top-[-1.5px]"
                colorClassName="text-fg-secondary"
                labelAndStarsContainerClassName="gap-x-1 md:gap-x-2"
            />
        ) : null

    const tagsNode = (
        <RestaurantTags
            tags={tagNames}
            className="text-p-sm md:text-p-md text-fg-secondary w-fit"
        />
    )

    const isLongTitle = restaurant.name?.length && restaurant.name.length > 20

    return (
        <div className="flex flex-col md:flex-row justify-between w-full md:items-end">
            <div className="flex flex-col">
                <h1
                    className={cn('text-display-sm', isLongTitle ? 'mb-2 md:mb-1' : 'mb-0 md:mb-1')}
                >
                    {restaurant.name}
                </h1>

                <div className="flex flex-col md:flex-row gap-x-3 md:mt-1 md:items-center">
                    <span className={cn('hidden', !ratingNode ? 'hidden' : 'md:block')}>
                        {ratingNode}
                    </span>

                    <span className="md:hidden">{tagsNode}</span>

                    <div className="flex flex-row mt-1 md:mt-0 items-center">
                        <SeparationLine
                            className={cn(
                                'text-border-primary md:min-h-[16px]',
                                ratingNode ? 'hidden md:block md:mr-3' : 'hidden'
                            )}
                        />

                        <span className="hidden md:pr-3 md:block">{tagsNode}</span>

                        <span className={cn('md:hidden', ratingNode && 'pr-2 md:pr-0')}>
                            {ratingNode}
                        </span>

                        <SeparationLine
                            className={cn(
                                'text-border-primary md:min-h-[16px]',
                                (!tagsNode || isMobile) && !ratingNode ? 'hidden' : 'md:mr-3 mr-2'
                            )}
                        />

                        <RestaurantPriceRange
                            priceRange={restaurant.priceRange ?? null}
                            currencySymbol={restaurant.city?.country?.currency?.symbol || '€'}
                            className="text-p-sm md:text-p-md"
                            filledColorClassName="text-fg-primary"
                            emptyColorClassName="text-fg-disabled"
                        />
                    </div>
                </div>
            </div>

            <RestaurantDetailMobileCta className="mt-3" />

            <div className="hidden md:flex flex-col gap-y-2.5 md:gap-y-1.5 md:items-end">
                <RestaurantDetailLocation />

                <RestaurantDetailOpeningHours styleType="collapsible" />
            </div>
        </div>
    )
}
